import { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import HinglishTextarea from './HinglishTextarea';
import { getHitRatesSummary, getHitRatePhrases, getHitRateClips } from '../services/api';

const PAGE = 50;
// Add [id, label] here when another STT model's results are available; the id is sent as `model`.
const MODELS = [['omli_qwen', 'Omli_qwen']];
const SEGMENTS = ['', '3-5', '6-12', 'other'];
const MODES = [['exact', 'Exact'], ['lcs', 'LCS']];
const MUTED = 'var(--text-muted,#888)';
const BORDER = '1px solid var(--border)';
const GOOD = '#22c55e';
const BAD = '#ef4444';
const chip = (on) => ({
  background: on ? 'var(--accent)' : 'transparent', color: on ? '#fff' : 'var(--text)',
  border: BORDER, borderRadius: 6, padding: '4px 12px', fontSize: 13, cursor: 'pointer',
});
const SECTION = { fontSize: 13, fontWeight: 700, margin: '0 0 6px', color: MUTED, textTransform: 'uppercase' };

const errorMessage = (e) => {
  const d = e.response?.data?.detail;
  return typeof d === 'string' ? d : e.message;
};

function Choice({ options, value, onChange }) {
  return options.map(([k, label]) => (
    <button key={k} onClick={() => onChange(k)} style={chip(value === k)}>{label}</button>
  ));
}

function Card({ label, value }) {
  return (
    <div style={{ border: BORDER, borderRadius: 8, padding: '8px 14px', minWidth: 110 }}>
      <div style={{ fontSize: 12, color: MUTED }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function Bar({ pct }) {
  return (
    <div style={{ width: 70, height: 8, background: 'var(--border)', borderRadius: 4, flexShrink: 0 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: pct >= 50 ? GOOD : BAD, borderRadius: 4 }} />
    </div>
  );
}

// Reference and hypothesis rows; the hypothesis turns red when it differs.
function TextPair({ title, reference, hypothesis, miss }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 15, marginTop: 4 }}>
      <span style={{ width: 70, color: MUTED, fontSize: 12 }}>{title}</span>
      <span style={{ flex: 1 }}><span style={{ color: MUTED, fontSize: 12 }}>ref </span>{reference || '—'}</span>
      <span style={{ flex: 1, color: miss ? BAD : 'inherit' }}><span style={{ color: MUTED, fontSize: 12 }}>hyp </span>{hypothesis || '—'}</span>
    </div>
  );
}

export default function HitRates({ user, onPageChange }) {
  const [model, setModel]     = useState(MODELS[0][0]);
  const [segment, setSegment] = useState('');
  const [input, setInput]     = useState('');
  const [typed, setTyped]     = useState('');      // English typed here becomes Hindi on space
  const [q, setQ]             = useState('');      // committed on Enter / Search
  const [mode, setMode]       = useState('exact');
  const [misses, setMisses]   = useState(false);   // only clips the model got wrong
  const [phrase, setPhrase]   = useState(null);    // normalized reference picked in the stats panel
  const [clips, setClips]     = useState({ total: 0, items: [] });
  const [offset, setOffset]   = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [summary, setSummary] = useState(null);
  const [totalClips, setTotalClips] = useState(null); // all clips in the segment, scored or not
  const [phrases, setPhrases] = useState([]);
  const [sort, setSort]       = useState('count');
  const [asc, setAsc]         = useState(false);
  const [error, setError]     = useState(null);

  const fail = (e) => setError(errorMessage(e));

  useEffect(() => { setOffset(0); }, [model, segment, q, mode, misses, phrase]);

  // Presigned audio URLs expire, so every page refetches its clips.
  // `stale` drops responses from superseded requests (e.g. the pre-reset offset fetch).
  useEffect(() => {
    let stale = false;
    getHitRateClips({
      model,
      q: q || undefined,
      q_mode: q ? mode : undefined,
      ref: phrase || undefined,
      age_segment: segment || undefined,
      exact_hit: misses ? false : undefined,
      offset,
      limit: PAGE,
    }).then((r) => { if (!stale) { setClips(r); setError(null); } }).catch((e) => { if (!stale) fail(e); });
    return () => { stale = true; };
  }, [model, segment, q, mode, misses, phrase, offset]);

  // Stats load only once the panel is opened.
  useEffect(() => {
    if (!showStats) return;
    getHitRatesSummary({ model }).then(setSummary).catch(fail);
  }, [showStats, model]);

  // Summary counts scored clips only, so the unscored count is this total minus overall.clips.
  useEffect(() => {
    if (!showStats) return;
    getHitRateClips({ model, age_segment: segment || undefined, limit: 1 }).then((r) => setTotalClips(r.total)).catch(fail);
  }, [showStats, model, segment]);

  useEffect(() => {
    if (!showStats) return;
    getHitRatePhrases({ model, max_words: 2, min_count: 5, age_segment: segment || undefined })
      .then((r) => setPhrases(r.items))
      .catch(fail);
  }, [showStats, model, segment]);

  const overall = segment ? summary?.by_age_segment?.[segment]?.overall : summary?.overall;
  // Clicking the active sort button flips its direction; a new one starts descending.
  const pickSort = (key) => { setAsc(key === sort ? !asc : false); setSort(key); };
  const sortValue = { count: (p) => p.clips, hit_rate: (p) => p.hit_rate_pct, misses: (p) => p.clips - p.exact_hits }[sort];
  const sortedPhrases = [...phrases].sort((a, b) => (asc ? 1 : -1) * (sortValue(a) - sortValue(b)) || b.clips - a.clips);
  const unscored = overall && totalClips != null ? totalClips - overall.clips : 0;

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="hit-rates"
        onPageChange={onPageChange}
        title={user?.name || user?.email || 'Reviewer'}
        subtitle="STT Validation Results"
      />
      <main className="annotator" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="annotator-header" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1 style={{ margin: 0 }}>STT Validation Results</h1>
          <Choice options={MODELS} value={model} onChange={setModel} />
          <button onClick={() => setShowStats((s) => !s)} style={chip(showStats)}>Hit rate & stats</button>
        </div>

        {error && <div style={{ color: BAD, padding: '8px 16px' }}>{error}</div>}

        {/* Type English, space converts each word to Hindi; send it to the search box. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 16px 0', flexShrink: 0 }}>
          <HinglishTextarea
            lang="hi"
            rows={1}
            placeholder="Type in English, press space to convert to Hindi"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            style={{ width: 360, fontSize: 15 }}
          />
          <button type="button" disabled={!typed.trim()} onClick={() => setInput(typed.trim())} style={chip(false)}>Use in search</button>
        </div>

        {/* Search */}
        <form
          onSubmit={(e) => { e.preventDefault(); setQ(input.trim()); }}
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '8px 16px', flexShrink: 0 }}
        >
          <input placeholder="Word in raw ref or hyp" value={input} onChange={(e) => setInput(e.target.value)} style={{ width: 240, fontSize: 15 }} />
          <button type="submit" style={chip(true)}>Search</button>
          <Choice options={MODES} value={mode} onChange={setMode} />
          <Choice options={SEGMENTS.map((s) => [s, s || 'All ages'])} value={segment} onChange={setSegment} />
          <button type="button" onClick={() => setMisses((m) => !m)} style={chip(misses)}>Misses only</button>
        </form>

        {showStats && (
          <div style={{ borderTop: BORDER, borderBottom: BORDER, padding: 16, maxHeight: '40%', overflowY: 'auto', flexShrink: 0 }}>
            {overall && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <Card label="Clips" value={overall.clips} />
                <Card label="Exact hit" value={`${overall.exact_hit_rate_pct}%`} />
                <Card label="Word recall" value={`${overall.word_recall_pct}%`} />
                <Card label="WER" value={`${overall.wer_pct}%`} />
                <Card label="Empty hyp" value={`${overall.empty_pct}%`} />
              </div>
            )}
            {unscored > 0 && (
              <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>
                Stats count scored clips only. {unscored} of the {totalClips} clips are excluded because their reference is empty after normalization (only filler words like ये, मैं, या, हम्म).
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{ ...SECTION, margin: 0, flex: 1 }}>Short phrases (≤2 words) · click to filter clips</div>
              <Choice options={[['count', 'Count'], ['hit_rate', 'Hit %'], ['misses', 'Misses']].map(([k, l]) => [k, k === sort ? `${l} ${asc ? '↑' : '↓'}` : l])} value={sort} onChange={pickSort} />
              {phrase && <button onClick={() => setPhrase(null)} style={chip(false)}>Clear phrase</button>}
            </div>
            {sortedPhrases.map((p) => (
              <div
                key={p.phrase}
                onClick={() => setPhrase(phrase === p.phrase ? null : p.phrase)}
                style={{ padding: '5px 0', borderBottom: BORDER, cursor: 'pointer', fontWeight: phrase === p.phrase ? 700 : 400 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 160 }}>{p.phrase}</span>
                  <span style={{ color: MUTED, fontSize: 12, width: 70 }}>{p.exact_hits}/{p.clips}</span>
                  <Bar pct={p.hit_rate_pct} />
                  <span style={{ width: 52, fontSize: 13 }}>{p.hit_rate_pct}%</span>
                  <span style={{ flex: 1, fontSize: 12, color: MUTED }}>
                    {p.top_misses.map((m) => `${m.text} (${m.count})`).join(', ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Clips */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <strong>{clips.total} clips</strong>
            {q && <span style={{ color: MUTED, fontSize: 13 }}>for “{q}” ({mode})</span>}
            {phrase && <button onClick={() => setPhrase(null)} style={chip(true)}>ref = {phrase} ✕</button>}
            <span style={{ flex: 1 }} />
            <button disabled={offset === 0} onClick={() => setOffset(offset - PAGE)} style={chip(false)}>Prev</button>
            <span style={{ fontSize: 13 }}>{clips.total ? offset + 1 : 0}–{Math.min(offset + PAGE, clips.total)}</span>
            <button disabled={offset + PAGE >= clips.total} onClick={() => setOffset(offset + PAGE)} style={chip(false)}>Next</button>
          </div>

          {clips.items.map((c) => (
            <div key={c.row} style={{ borderBottom: BORDER, padding: '10px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <strong>Row {c.row}</strong>
                <span style={{ ...chip(false), cursor: 'default', background: c.exact_hit ? GOOD : BAD, color: '#fff', border: 'none' }}>
                  {c.exact_hit ? 'Hit' : 'Miss'}
                </span>
                <span style={{ color: MUTED, fontSize: 13 }}>
                  age {c.age ?? '—'} · verified by {c.vetted_by || '—'} · WER {c.wer_pct}%{c.lcs_score != null && ` · LCS ${c.lcs_score.toFixed(2)}`}
                </span>
              </div>
              {c.audio_url
                ? <audio src={c.audio_url} controls preload="none" style={{ width: '100%' }} />
                : <div style={{ fontSize: 13, color: MUTED }}>Audio unavailable</div>}
              <TextPair title="Raw" reference={c.reference_raw} hypothesis={c.hypothesis_raw} miss={!c.exact_hit} />
              <TextPair title="Normalized" reference={c.reference} hypothesis={c.hypothesis} miss={!c.exact_hit} />
            </div>
          ))}
          {clips.items.length === 0 && <div style={{ color: MUTED }}>No clips.</div>}
        </div>
      </main>
    </div>
  );
}
